import tenantConstants from '@constants';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, ErrorMessage, Group, TextInput } from '../../common';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Label from '../../common/Label/Label';
import { IconStyled } from '../../common/icon/IconStyled';
import Icon from '../../common/icon/icon';
import { FieldIconBox } from '../../common/image-uploads/styled';
import MediaGalleryModal from '../media-gallery/media-gallery-modal';
import {
  AddVideo,
  DeleteVideoBtn,
  VideoCard,
  VideoDesc,
  VideoInputBox,
  VideoInputLine,
  VideoPreview,
  VideoTitle,
} from './styled';
import { v2Font } from '@theme/v2';

const REDESIGN_FORM = !!tenantConstants?.FORM;
const MAX_VIDEOS = 4;

const YOUTUBE_ID_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/;

const getYoutubeId = (url) => {
  if (!url) return null;
  const match = String(url).match(YOUTUBE_ID_RE);
  return match ? match[1] : null;
};

/* Compact YouTube glyph (matches Figma youtube.svg — red badge + white play triangle). */
const YoutubeGlyph = () => (
  <svg width="17" height="12" viewBox="0 0 24 17" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path
      d="M23.5 2.64a3.01 3.01 0 0 0-2.12-2.13C19.5 0 12 0 12 0S4.5 0 2.62.51A3.01 3.01 0 0 0 .5 2.64 31.3 31.3 0 0 0 0 8.5a31.3 31.3 0 0 0 .5 5.86 3.01 3.01 0 0 0 2.12 2.13C4.5 17 12 17 12 17s7.5 0 9.38-.51a3.01 3.01 0 0 0 2.12-2.13A31.3 31.3 0 0 0 24 8.5a31.3 31.3 0 0 0-.5-5.86Z"
      fill="#FF0000"
    />
    <path d="M9.55 12.14 15.82 8.5 9.55 4.86v7.28Z" fill="#fff" />
  </svg>
);

/**
 * One YouTube link row (Bayut redesign): teal card with [youtube glyph | url input | delete],
 * and — once a valid link is present — a preview card (thumbnail + title + channel).
 * Title/channel are fetched best-effort from YouTube oEmbed; falls back to stored values.
 */
// Title / channel of a YouTube video, best-effort from oEmbed (null until it answers).
const useYoutubeMeta = (videoId) => {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    if (!videoId) {
      setMeta(null);
      return undefined;
    }
    let active = true;
    fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data) setMeta({ title: data.title, author: data.author_name });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [videoId]);
  return meta;
};

const VideoLinkRow = ({ item, index, value, valueKey, setFieldValue, errorMsg, onPreview, t }) => {
  const url = item?.url || item?.link || '';
  const videoId = getYoutubeId(url);
  const meta = useYoutubeMeta(videoId);

  const title = meta?.title || item?.title || '';
  const channel = meta?.author || item?.channel || item?.author || '';
  const thumb = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;

  return (
    <VideoCard>
      <VideoInputLine>
        <YoutubeGlyph />
        <VideoInputBox>
          <input
            value={url}
            placeholder={t('Paste youtube video link here')}
            onChange={(e) => {
              const next = [...value];
              const newUrl = e.target.value;
              next[index] = { ...item, url: newUrl, ...(newUrl === '' && { link: '' }) };
              setFieldValue(valueKey, next, true);
            }}
          />
        </VideoInputBox>
        <DeleteVideoBtn
          $filled
          type="button"
          aria-label={t('Delete Video')}
          onClick={() => {
            const next = [...value];
            next[index] = { ...next[index], _destroy: true };
            setFieldValue(valueKey, next, true);
          }}
        >
          <Icon icon="RxCross2" size={14} color="#F73131" />
        </DeleteVideoBtn>
      </VideoInputLine>

      {!!videoId && (
        // Click the video preview to open the same gallery as the listing preview (Videos tab).
        <VideoPreview onClick={onPreview} style={onPreview ? { cursor: 'pointer' } : undefined}>
          {thumb && <img src={thumb} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />}
          <div className="meta">
            <p className="title">{title || url}</p>
            {channel && <p className="channel">{channel}</p>}
          </div>
        </VideoPreview>
      )}

      {!!errorMsg && <ErrorMessage message={errorMsg} />}
    </VideoCard>
  );
};

/* Trailing "Paste youtube video link here" row — keeps its own draft so typing never
   loses focus; commits a new entry on a complete YouTube link or on blur. */
const AddVideoRow = ({ value, valueKey, setFieldValue, hostList, t }) => {
  const [draft, setDraft] = useState('');

  const commit = (val) => {
    const next = (val || '').trim();
    if (!next) return;
    setFieldValue(valueKey, [...(value || []), { host: hostList?.[0]?.value, url: next, title: '' }], true);
    setDraft('');
  };

  return (
    <VideoCard>
      <VideoInputLine>
        <YoutubeGlyph />
        <VideoInputBox>
          <input
            value={draft}
            placeholder={t('Paste youtube video link here')}
            onChange={(e) => {
              const val = e.target.value;
              setDraft(val);
              if (getYoutubeId(val)) commit(val);
            }}
            onBlur={() => commit(draft)}
          />
        </VideoInputBox>
        <DeleteVideoBtn $filled={false} type="button" disabled aria-hidden="true" tabIndex={-1}>
          <Icon icon="RxCross2" size={14} color="#707070" />
        </DeleteVideoBtn>
      </VideoInputLine>
    </VideoCard>
  );
};

/* ------------------------------------------------------------------------------------------------
 * compactPreview (opt-in, Listings V2 drawer): read-only cards [thumbnail | title, channel, link |
 * delete] and one "paste a link + Add" row. 'desktop' or 'mobile' picks the design's sizes.
 * ---------------------------------------------------------------------------------------------- */
const COMPACT_SIZES = {
  desktop: { pad: 10, gap: 12, thumbW: 96, thumbH: 60, play: 24, title: 14, channel: 12, link: 11, del: 32, delIcon: 17, inputH: 42, inputFont: 13, btnPad: 18, btnFont: 13, btnWeight: 600, stack: 12, radius: 6 },
  mobile: { pad: 8, gap: 10, thumbW: 72, thumbH: 48, play: 20, title: 13, channel: 11, link: 10, del: 30, delIcon: 16, inputH: 40, inputFont: 12, btnPad: 14, btnFont: 12, btnWeight: 700, stack: 10, radius: 5 },
};

const CompactCard = styled.div`
  padding: ${(p) => p.$s.pad}px;
  background: white;
  border-radius: 8px;
  outline: 1px #e6e6e6 solid;
  outline-offset: -1px;
  display: flex;
  align-items: center;
  gap: ${(p) => p.$s.gap}px;
  min-width: 0;
`;
const CompactThumb = styled.button`
  position: relative;
  width: ${(p) => p.$s.thumbW}px;
  height: ${(p) => p.$s.thumbH}px;
  flex: none;
  padding: 0;
  border: none;
  border-radius: ${(p) => p.$s.radius}px;
  overflow: hidden;
  background: repeating-linear-gradient(135deg, #dcdcdc 0 6px, #e6e6e6 6px 12px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.28);
  }
  .play {
    position: relative;
    z-index: 1;
    width: ${(p) => p.$s.play}px;
    height: ${(p) => p.$s.play}px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.92);
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const CompactDelete = styled.button`
  width: ${(p) => p.$s.del}px;
  height: ${(p) => p.$s.del}px;
  flex: none;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: #fff2f2;
  }
`;
const CompactInput = styled.input`
  flex: 1 1 0;
  min-width: 0;
  height: ${(p) => p.$s.inputH}px;
  padding: 0 12px;
  background: white;
  border: none;
  border-radius: 6px;
  outline: 1px ${(p) => (p.$error ? '#CE2929' : '#E6E6E6')} solid;
  outline-offset: -1px;
  color: #222222;
  font-family: inherit;
  font-size: ${(p) => p.$s.inputFont}px;
  &:focus {
    outline: 1.5px ${(p) => (p.$error ? '#CE2929' : '#28B16D')} solid;
  }
`;
const CompactAddBtn = styled.button`
  flex: none;
  height: ${(p) => p.$s.inputH}px;
  padding: 0 ${(p) => p.$s.btnPad}px;
  border: none;
  border-radius: 6px;
  background: #28b16d;
  color: white;
  font-family: inherit;
  font-size: ${(p) => p.$s.btnFont}px;
  font-weight: ${(p) => p.$s.btnWeight};
  cursor: pointer;
  &:hover {
    background: #208e57;
  }
`;

const TrashGlyph = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4 5.5h12M8 5.5V4h4v1.5M5.5 5.5l.7 10a1.5 1.5 0 0 0 1.5 1.4h4.6a1.5 1.5 0 0 0 1.5-1.4l.7-10"
      stroke="#CE2929"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The link as the design prints it: no protocol, no "www.".
const shortLink = (url) => String(url || '').replace(/^https?:\/\//i, '').replace(/^www\./i, '');

const CompactVideoCard = ({ item, s, onRemove, onPreview, t }) => {
  const url = item?.url || item?.link || '';
  const videoId = getYoutubeId(url);
  const meta = useYoutubeMeta(videoId);
  const title = meta?.title || item?.title || shortLink(url);
  const channel = meta?.author || item?.channel || item?.author || '';
  return (
    <CompactCard $s={s}>
      <CompactThumb type="button" $s={s} aria-label={t('Play video')} onClick={onPreview}>
        {videoId && <img src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />}
        <span className="play">
          <svg width={Math.round(s.play * 0.38)} height={Math.round(s.play * 0.45)} viewBox="0 0 14 16" fill="#222222" aria-hidden="true">
            <path d="M0 0 13 8 0 16Z" />
          </svg>
        </span>
      </CompactThumb>
      <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: s === COMPACT_SIZES.mobile ? 2 : 3 }}>
        <div style={{ color: '#222222', fontFamily: v2Font('Figtree, sans-serif'), fontSize: s.title, fontWeight: 600, lineHeight: '20px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </div>
        {channel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <YoutubeGlyph />
            <span style={{ color: '#626262', fontSize: s.channel, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{channel}</span>
          </div>
        )}
        <div dir="ltr" style={{ color: '#9D9D9D', fontSize: s.link, fontWeight: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'start' }}>
          {shortLink(url)}
        </div>
      </div>
      <CompactDelete type="button" $s={s} title={t('Remove video')} aria-label={t('Remove video')} onClick={onRemove}>
        <TrashGlyph size={s.delIcon} />
      </CompactDelete>
    </CompactCard>
  );
};

const CompactVideos = ({ variant, value, valueKey, setFieldValue, hostList, onPreview, t }) => {
  const s = COMPACT_SIZES[variant] || COMPACT_SIZES.desktop;
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const live = (value || []).map((item, index) => ({ item, index })).filter(({ item }) => !item?._destroy);
  const canAddMore = live.length < MAX_VIDEOS;

  const add = () => {
    const url = draft.trim();
    if (!getYoutubeId(url)) {
      setInvalid(true);
      return;
    }
    setFieldValue(valueKey, [...(value || []), { host: hostList?.[0]?.value, url, title: '' }], true);
    setDraft('');
    setInvalid(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: s.stack, minWidth: 0 }}>
      {live.map(({ item, index }, i) => (
        <CompactVideoCard
          key={index}
          item={item}
          s={s}
          t={t}
          onPreview={() => onPreview(i)}
          onRemove={() => {
            const next = [...value];
            next[index] = { ...next[index], _destroy: true };
            setFieldValue(valueKey, next, true);
          }}
        />
      ))}
      {canAddMore && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CompactInput
              $s={s}
              $error={invalid}
              dir="ltr"
              value={draft}
              placeholder={live.length ? t('Paste another YouTube link') : t('Paste a YouTube link')}
              onChange={(e) => {
                setDraft(e.target.value);
                if (invalid) setInvalid(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <CompactAddBtn type="button" $s={s} onClick={add}>
              {variant === 'mobile' ? t('Add') : t('Add Video')}
            </CompactAddBtn>
          </div>
          {invalid && <span style={{ color: '#CE2929', fontSize: 11, lineHeight: '15px' }}>{t('Enter a valid YouTube link')}</span>}
        </div>
      )}
      {variant !== 'mobile' && (
        <div style={{ color: '#9D9D9D', fontSize: 11, lineHeight: '15px' }}>
          {t('A walkthrough video keeps buyers on your listing longer and lifts your quality score.')}
        </div>
      )}
    </div>
  );
};

const VideoSelect = (props) => {
  // compactPreview: 'desktop' | 'mobile' opts into the compact card layout (Listings V2 drawer);
  // unset keeps the post-listing layouts below unchanged.
  const { label, labelIcon, value, allValues, setFieldValue, style, errorMsg, valueKey, hostList, isMobile, compactPreview } = props;
  const { t } = useTranslation();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryInitialVidIndex, setGalleryInitialVidIndex] = useState(0);

  const videos = useMemo(() => (value ? value.filter((e) => !e._destroy) : []), [value]);
  const usableVids = useMemo(
    () => (value || []).filter((v) => !v?._destroy && getYoutubeId(v?.url || v?.link)),
    [value],
  );

  if (compactPreview) {
    return (
      <>
        <CompactVideos
          variant={compactPreview}
          value={value}
          valueKey={valueKey}
          setFieldValue={setFieldValue}
          hostList={hostList}
          t={t}
          onPreview={(i) => {
            setGalleryInitialVidIndex(i);
            setGalleryOpen(true);
          }}
        />
        <MediaGalleryModal
          visible={galleryOpen}
          initialTab="videos"
          initialIndex={galleryInitialVidIndex}
          onClose={() => setGalleryOpen(false)}
          images={allValues?.property_images || allValues?.images || []}
          videos={value || []}
        />
      </>
    );
  }

  // Bayut redesign — full Figma layout (gray-box field icon, teal link rows, preview cards).
  if (REDESIGN_FORM) {
    const canAddMore = videos.length < MAX_VIDEOS;
    return (
      <Group gap="16px" template="max-content minmax(0, 1fr)" style={{ ...style }}>
        <FieldIconBox>
          <Icon icon={labelIcon || 'AiOutlineVideoCamera'} color="#707070" size={17} />
        </FieldIconBox>
        <Group gap="12px" template="minmax(0, 1fr)">
          <div name={valueKey} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <VideoTitle>{label}</VideoTitle>
            <VideoDesc>{t('Bring your property to life. Add a youtube tour link below.')}</VideoDesc>
          </div>

          {(value || []).map((item, index) => {
            if (item._destroy) return null;
            const vidIdx = usableVids.findIndex((v) => v === item);
            return (
              <VideoLinkRow
                key={index}
                item={item}
                index={index}
                value={value}
                valueKey={valueKey}
                setFieldValue={setFieldValue}
                errorMsg={errorMsg?.[index]}
                onPreview={() => {
                  setGalleryInitialVidIndex(vidIdx >= 0 ? vidIdx : 0);
                  setGalleryOpen(true);
                }}
                t={t}
              />
            );
          })}

          {/* Trailing empty row to add another link (matches Figma "Paste youtube video link here"). */}
          {canAddMore && (
            <AddVideoRow value={value} valueKey={valueKey} setFieldValue={setFieldValue} hostList={hostList} t={t} />
          )}
        </Group>

        <MediaGalleryModal
          visible={galleryOpen}
          initialTab="videos"
          initialIndex={galleryInitialVidIndex}
          onClose={() => setGalleryOpen(false)}
          images={allValues?.property_images || allValues?.images || []}
          videos={value || []}
        />
      </Group>
    );
  }

  return (
    <Group gap="16px" template="max-content auto">
      {labelIcon && (
        <IconStyled>
          <Icon icon={labelIcon} />
        </IconStyled>
      )}
      <Group gap="8px" template="initial">
        <div name={valueKey}>
          <Label>{label}</Label>
          <div className="text-muted">
            {t('Add videos of your property from Youtube. Upload on Youtube and paste the link below.')}
          </div>
        </div>
        {!!videos.length && (
          <AddVideo gap="16px" template={'auto'}>
            {value.map((item, index) => {
              return (
                !item._destroy && (
                  <div style={{ position: 'relative' }} key={index}>
                    <TextInput
                      prefixIcon={
                        <Icon
                          icon={hostList.find((e) => e.value == item.host)?.icon || 'AiFillYoutube'}
                          color={hostList.find((e) => e.value == item.host)?.color || '#FF0000'}
                          size="1.2em"
                        />
                      }
                      label={t('Video Link')}
                      muted
                      placeholder={t('Place video link here')}
                      value={item.url || item.link || ''}
                      onChange={(e) => {
                        const fieldValue = [...value];
                        fieldValue[index] = { ...item, url: e.target.value, ...(e.target.value === '' && { link: '' }) };
                        setFieldValue(valueKey, fieldValue, true);
                      }}
                    />
                    <Button
                      type="link"
                      className="btn-close"
                      icon="HiOutlineTrash"
                      size="small"
                      style={{ '--btn-content-color': '#f5222d' }}
                      onClick={() => {
                        const fieldValue = [...value];
                        fieldValue[index] = { ...fieldValue[index], _destroy: true };
                        setFieldValue(valueKey, fieldValue, true);
                      }}
                    >
                      {t('Delete Video')}
                    </Button>
                    {!!errorMsg?.[index] && <ErrorMessage message={errorMsg?.[index]} />}
                  </div>
                )
              );
            })}
          </AddVideo>
        )}
        {(!value || videos?.length <= 3) && (
          <div>
            <Button
              type="primary"
              outlined
              onClick={() => {
                setFieldValue(valueKey, [...(value || []), { host: hostList[0].value, url: '', title: '' }]);
              }}
              style={{ fontSize: 14 }}
            >
              {videos?.length > 0 ? t('Add Another Video') : t('Add Video')}
            </Button>
          </div>
        )}
      </Group>
    </Group>
  );
};

export default VideoSelect;
